// frontend/src/hooks/useParameterSearch.js
// Custom hook for parameter search functionality

import { useState, useEffect, useMemo, useCallback } from 'react';
import { buildParameterRegistry, searchParameters, getCategoryKeyForNavigation, formatParameterForDisplay } from '../utils/parameterSearchUtils';

/**
 * Custom hook for parameter search functionality
 * @param {Function} onParameterSelect - Callback when parameter is selected
 * @param {number} debounceMs - Debounce delay in milliseconds
 * @returns {Object} - Search state and functions
 */
export function useParameterSearch(onParameterSelect, debounceMs = 25) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Build parameter registry once on mount
  const parameterRegistry = useMemo(() => {
    try {
      return buildParameterRegistry();
    } catch (error) {
      console.error('Error building parameter registry:', error);
      return [];
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        setIsSearching(true);
        try {
          const results = searchParameters(parameterRegistry, searchQuery, 10);
          const formattedResults = results.map(formatParameterForDisplay);
          setSearchResults(formattedResults);
          setShowResults(true);
          setSelectedIndex(-1);
        } catch (error) {
          console.error('Error searching parameters:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
        setSelectedIndex(-1);
        setIsSearching(false);
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, parameterRegistry, debounceMs]);

  // Handle search input change
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  // Handle parameter selection
  const handleParameterSelect = useCallback((parameter) => {
    if (onParameterSelect) {
      const categoryKey = getCategoryKeyForNavigation(parameter);
      onParameterSelect(parameter, categoryKey);
    }
    
    // Clear search after selection
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    setSelectedIndex(-1);
  }, [onParameterSelect]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (!showResults || searchResults.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleParameterSelect(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setShowResults(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  }, [showResults, searchResults, selectedIndex, handleParameterSelect]);

  // Handle search input focus
  const handleSearchFocus = useCallback(() => {
    if (searchResults.length > 0) {
      setShowResults(true);
    }
  }, [searchResults.length]);

  // Handle search input blur (with delay to allow for clicks)
  const handleSearchBlur = useCallback(() => {
    setTimeout(() => {
      setShowResults(false);
      setSelectedIndex(-1);
    }, 150);
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    setSelectedIndex(-1);
    setIsSearching(false);
  }, []);

  // Get highlighted result for keyboard navigation
  const getHighlightedResult = useCallback((index) => {
    return selectedIndex === index;
  }, [selectedIndex]);

  return {
    // State
    searchQuery,
    searchResults,
    isSearching,
    showResults,
    selectedIndex,
    
    // Functions
    handleSearchChange,
    handleParameterSelect,
    handleKeyDown,
    handleSearchFocus,
    handleSearchBlur,
    clearSearch,
    getHighlightedResult,
    
    // Computed
    hasResults: searchResults.length > 0,
    registrySize: parameterRegistry.length,
  };
}
